import { FileDownloadRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { Workbook } from 'exceljs';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useRef, useState } from 'react';

import { uploadCollection } from '../../../../services/dealGPT';
import { StyledIconButton } from '../../../../theme/CustomComponents';
import { downloadExcel } from '../../../../util/excel';

interface ImportCollectionDialogProps {
  onClose: () => void;
  onImportCompleted: (collectionId: string) => void;
}

const ImportCollectionDialog: React.FC<ImportCollectionDialogProps> = ({
  onClose,
  onImportCompleted,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const downloadTemplate = useCallback(async () => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Template');

    worksheet.columns = [
      { header: 'Collection', key: 'collection', width: 30 },
      { header: 'Category', key: 'category', width: 30 },
      { header: 'Question', key: 'question', width: 50 },
      { header: 'Custom Prompt', key: 'customPrompt', width: 50 },
    ];

    // Optional: sample row
    worksheet.addRow({
      collection: 'Sample Collection',
      category: 'General',
      question: 'What is the annual revenue?',
    });

    // Apply Arial 12 font and wrap text to all cells
    worksheet.eachRow((row, rowNumber) => {
      row.eachCell((cell) => {
        cell.font = {
          name: 'Arial',
          size: 12,
          bold: rowNumber === 1,
        };
        cell.alignment = {
          wrapText: true,
          vertical: 'top',
        };
      });
    });

    await downloadExcel(workbook, 'collection_template.xlsx');
  }, []);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.[0]) {
      setFile(event.target.files[0]);
    }
  }, []);

  const handleImport = useCallback(async () => {
    if (!file) return;

    setErrors([]);
    setIsProcessing(true);
    try {
      const { created, errors } = await uploadCollection(file);

      if (errors && errors.length > 0) {
        setErrors(errors);
        setFile(null);
        // Reset the file input's value so the same file can be selected again
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else if (created) {
        enqueueSnackbar(`Collection: ${created.name} imported successfully.`, {
          variant: 'success',
        });
        await onImportCompleted(created.id);
      }
    } catch {
      enqueueSnackbar('Failed to import collection.', { variant: 'error' });
    } finally {
      onClose();
      setIsProcessing(false);
    }
  }, [file, onClose, onImportCompleted]);

  return (
    <Dialog open fullWidth onClose={onClose}>
      <DialogTitle>
        Import Collection{' '}
        <Tooltip title="Download template" arrow>
          <StyledIconButton onClick={downloadTemplate}>
            <FileDownloadRounded />
          </StyledIconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        <Typography variant="caption">
          Use the provided template to ensure all columns are correctly formatted for the Collection
          import.
        </Typography>

        <Box mt={3}>
          <input
            ref={fileInputRef}
            accept=".xlsx"
            id="upload-file"
            type="file"
            style={{ display: 'none' }}
            onChange={handleFileChange}
            disabled={isProcessing}
          />
          <label htmlFor="upload-file">
            <Button variant="outlined" component="span" disabled={isProcessing}>
              Upload File
            </Button>
            {file && (
              <Typography variant="body2" sx={{ mt: 1, ml: 2, display: 'inline' }}>
                {file.name}
              </Typography>
            )}
          </label>
        </Box>

        {errors.length > 0 && (
          <Box mt={2}>
            <Typography variant="subtitle2" color="error">
              Errors:
            </Typography>
            <Box
              sx={{
                maxHeight: 200,
                overflowY: 'auto',
                border: '1px solid #f44336',
                borderRadius: 1,
                p: 1,
                mt: 1,
              }}
            >
              <List dense>
                {errors.map((error, idx) => (
                  <ListItem key={idx}>
                    <ListItemText
                      primary={error}
                      primaryTypographyProps={{ variant: 'body2', color: 'error' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isProcessing}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleImport}
          variant="contained"
          color="primary"
          loading={isProcessing}
          disabled={!file}
        >
          Import
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ImportCollectionDialog;
